import { useState } from "react";
import { Check, Stethoscope, BadgeCheck, Building2, Mail, Phone, MapPin, Save } from "lucide-react";
import {
  ALL_SPECIALITES,
  SPECIALITES,
  useDoctor,
  type DoctorProfile,
} from "@/lib/doctor-store";

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

const inputClass =
  "w-full rounded-xl border border-white/10 bg-white/5 px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/60 outline-none transition focus:border-success/60 focus:bg-white/[0.07]";

export default function ConfigurationView() {
  const [doctor, setDoctor] = useDoctor();
  const [draft, setDraft] = useState<DoctorProfile>(doctor);
  const [saved, setSaved] = useState(false);

  const set = <K extends keyof DoctorProfile>(k: K, v: DoctorProfile[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const onSave = () => {
    setDoctor(draft);
    setSaved(true);
    setTimeout(() => setSaved(false), 1800);
  };

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
      {/* MAIN FORM */}
      <div className="space-y-5">
        {/* Identité */}
        <div className="card-surface rounded-2xl p-5 sm:p-6">
          <div className="mb-5 flex items-center gap-2 text-sm text-muted-foreground">
            <BadgeCheck className="h-4 w-4 text-success" />
            Identité du praticien
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Nom">
              <input
                className={inputClass}
                value={draft.nom}
                onChange={(e) => set("nom", e.target.value)}
              />
            </Field>
            <Field label="Prénom">
              <input
                className={inputClass}
                value={draft.prenom}
                onChange={(e) => set("prenom", e.target.value)}
              />
            </Field>
            <Field label="N° INPE">
              <input
                className={inputClass}
                value={draft.inpe}
                onChange={(e) => set("inpe", e.target.value)}
              />
            </Field>
            <Field label="N° d'autorisation">
              <input
                className={inputClass}
                value={draft.numero_autorisation}
                onChange={(e) => set("numero_autorisation", e.target.value)}
              />
            </Field>
            <Field label="Date d'inscription">
              <input
                type="date"
                className={inputClass}
                value={draft.date_inscription}
                onChange={(e) => set("date_inscription", e.target.value)}
              />
            </Field>
            <Field label="Statut">
              <select
                className={inputClass}
                value={draft.statut}
                onChange={(e) => set("statut", e.target.value as DoctorProfile["statut"])}
              >
                <option>Actif</option>
                <option>Inactif</option>
                <option>Retraité</option>
              </select>
            </Field>
          </div>
        </div>

        {/* Spécialités */}
        <div className="card-surface rounded-2xl p-5 sm:p-6">
          <div className="mb-5 flex items-center gap-2 text-sm text-muted-foreground">
            <Stethoscope className="h-4 w-4 text-success" />
            Spécialités & exercice
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Spécialité principale">
              <select
                className={inputClass}
                value={draft.specialite_principale}
                onChange={(e) => set("specialite_principale", e.target.value)}
              >
                {(Object.keys(SPECIALITES) as Array<keyof typeof SPECIALITES>).map((g) => (
                  <optgroup key={g} label={g.toUpperCase()}>
                    {SPECIALITES[g].map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </Field>
            <Field label="Sous-spécialités (séparées par virgule)">
              <input
                className={inputClass}
                value={draft.sous_specialites.join(", ")}
                onChange={(e) =>
                  set(
                    "sous_specialites",
                    e.target.value
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean),
                  )
                }
              />
            </Field>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            {ALL_SPECIALITES.slice(0, 12).map((s) => {
              const active = draft.specialite_principale === s;
              return (
                <button
                  key={s}
                  onClick={() => set("specialite_principale", s)}
                  className={"chip " + (active ? "chip-active" : "")}
                >
                  {s}
                </button>
              );
            })}
          </div>

          <label className="mt-5 flex items-center gap-3 text-sm">
            <input
              type="checkbox"
              checked={draft.est_chirurgien}
              onChange={(e) => set("est_chirurgien", e.target.checked)}
              className="h-4 w-4 accent-[oklch(0.78_0.18_150)]"
            />
            Exercice chirurgical
          </label>
        </div>

        {/* Cabinet */}
        <div className="card-surface rounded-2xl p-5 sm:p-6">
          <div className="mb-5 flex items-center gap-2 text-sm text-muted-foreground">
            <Building2 className="h-4 w-4 text-success" />
            Cabinet & coordonnées
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Clinique / Hôpital">
              <input
                className={inputClass}
                value={draft.clinique_hopital}
                onChange={(e) => set("clinique_hopital", e.target.value)}
              />
            </Field>
            <Field label="Ville">
              <input
                className={inputClass}
                value={draft.ville}
                onChange={(e) => set("ville", e.target.value)}
              />
            </Field>
            <Field label="Adresse professionnelle">
              <input
                className={inputClass}
                value={draft.adresse_professionnelle}
                onChange={(e) => set("adresse_professionnelle", e.target.value)}
              />
            </Field>
            <Field label="Téléphone">
              <input
                className={inputClass}
                value={draft.telephone}
                onChange={(e) => set("telephone", e.target.value)}
              />
            </Field>
            <Field label="Email">
              <input
                className={inputClass}
                value={draft.email}
                onChange={(e) => set("email", e.target.value)}
              />
            </Field>
            <Field label="Fax">
              <input
                className={inputClass}
                value={draft.fax}
                onChange={(e) => set("fax", e.target.value)}
              />
            </Field>
            <Field label="ICE">
              <input
                className={inputClass}
                value={draft.ice}
                onChange={(e) => set("ice", e.target.value)}
              />
            </Field>
            <Field label="Identifiant Fiscal (IF)">
              <input
                className={inputClass}
                value={draft.if}
                onChange={(e) => set("if", e.target.value)}
              />
            </Field>
            <Field label="Taxe Professionnelle (TP)">
              <input
                className={inputClass}
                value={draft.tp}
                onChange={(e) => set("tp", e.target.value)}
              />
            </Field>
            <Field label="Faculté">
              <input
                className={inputClass}
                value={draft.faculte}
                onChange={(e) => set("faculte", e.target.value)}
              />
            </Field>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3">
          {saved && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-success/15 px-3 py-1.5 text-xs text-success">
              <Check className="h-3.5 w-3.5" />
              Enregistré — disponible dans E-notes
            </span>
          )}
          <button
            onClick={onSave}
            className="inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2.5 text-sm font-medium text-background hover:opacity-90"
          >
            <Save className="h-4 w-4" />
            Enregistrer le profil
          </button>
        </div>
      </div>

      {/* PREVIEW CARD */}
      <aside className="space-y-5">
        <div className="card-surface rounded-2xl p-5 sm:p-6">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            Aperçu praticien
          </div>
          <div className="mt-4 flex items-center gap-3">
            <div className="grid h-14 w-14 place-items-center rounded-full bg-gradient-to-br from-success/40 to-accent/40 text-lg font-medium">
              {draft.prenom[0]}
              {draft.nom[0]}
            </div>
            <div className="min-w-0">
              <div className="truncate text-base font-medium">
                Dr {draft.prenom} {draft.nom}
              </div>
              <div className="truncate text-xs text-muted-foreground">
                {draft.specialite_principale}
              </div>
            </div>
          </div>

          <dl className="mt-5 space-y-3 text-sm">
            <div className="flex items-start gap-2 text-muted-foreground">
              <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-success" />
              <span>
                INPE&nbsp;
                <span className="text-foreground">{draft.inpe || "—"}</span> · Autorisation&nbsp;
                <span className="text-foreground">{draft.numero_autorisation || "—"}</span>
              </span>
            </div>
            <div className="flex items-start gap-2 text-muted-foreground">
              <Building2 className="mt-0.5 h-4 w-4 shrink-0" />
              <span className="text-foreground">{draft.clinique_hopital}</span>
            </div>
            <div className="flex items-start gap-2 text-muted-foreground">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {draft.adresse_professionnelle}, {draft.ville}
              </span>
            </div>
            <div className="flex items-start gap-2 text-muted-foreground">
              <Phone className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{draft.telephone || "—"}</span>
            </div>
            <div className="flex items-start gap-2 text-muted-foreground">
              <Mail className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{draft.email || "—"}</span>
            </div>
          </dl>

          <div className="mt-5 flex flex-wrap gap-1.5">
            {draft.sous_specialites.map((s) => (
              <span key={s} className="chip text-xs">
                {s}
              </span>
            ))}
          </div>
        </div>

        <div className="card-surface rounded-2xl p-5 sm:p-6">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            Synchronisation
          </div>
          <p className="mt-3 text-sm text-muted-foreground">
            Les paramètres du praticien sont injectés automatiquement dans chaque{" "}
            <span className="text-foreground">E-note</span> (Feuille de Soins,
            ALD, Médicaments, Dentaire).
          </p>
        </div>
      </aside>
    </div>
  );
}
